/**
 * useVerifyToken mutation hook
 *
 * Updates a token's verification status via POST /v1/admin/tokens/:id/verify.
 * On success, invalidates admin tokens, admin stats, and token detail queries.
 *
 * Story 7.3 — Task 2.4
 */

import type { Token, VerificationStatus } from "@repo/shared-types";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";

import { trackClientEvent } from "../../lib/analytics";
import { adminApi } from "../../lib/api";
import { useWallet } from "../use-wallet";
import { adminKeys, tokenKeys } from "./query-keys";

type VerifyTokenInput = {
	tokenId: string;
	status: VerificationStatus;
	reason?: string;
};

type VerifyTokenResult = {
	token: Token;
	previousStatus: string;
};

export function useVerifyToken(): UseMutationResult<VerifyTokenResult, Error, VerifyTokenInput> {
	const queryClient = useQueryClient();
	const { getAccessToken, address } = useWallet();

	return useMutation({
		mutationFn: async ({ tokenId, status, reason }: VerifyTokenInput) => {
			const token = await getAccessToken();
			if (!token) {
				// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors
				throw new Error("No auth token");
			}
			const body: { status: VerificationStatus; reason?: string } = { status };
			if (reason) body.reason = reason;
			const response = await adminApi.verifyToken(tokenId, body, token);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			void queryClient.invalidateQueries({ queryKey: adminKeys.tokens() });
			void queryClient.invalidateQueries({ queryKey: adminKeys.stats() });
			void queryClient.invalidateQueries({ queryKey: tokenKeys.detail(variables.tokenId) });

			trackClientEvent("admin_token_verified", {
				tokenId: variables.tokenId,
				newStatus: variables.status,
				adminWallet: address ?? "",
			});
		},
	});
}
